import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Archive, Paperclip, Send, UserPlus, Users, X, RefreshCw, Eye, Table2, Columns3, Mic, CalendarDays, Download, Play, Square as SquareIcon, Clock, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["todo", "in_progress", "review", "done"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const VIEWS = [
  { key: "mine", label: "My Tasks" },
  { key: "team", label: "Team Tasks" },
  { key: "all", label: "All Tasks" },
  { key: "following", label: "Following" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Completed" },
];

function StatusPill({ status }) {
  const cfg = {
    todo: "bg-slate-100 text-slate-700",
    in_progress: "bg-cyan-100 text-cyan-700",
    review: "bg-amber-100 text-amber-700",
    done: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${cfg[status] || "bg-slate-100"}`}>{(status || "todo").replace("_", " ")}</span>;
}
function PriorityTag({ p }) {
  const cfg = { low: "text-slate-500", medium: "text-cyan-600", high: "text-amber-600", urgent: "text-red-600" };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider ${cfg[p] || "text-slate-500"}`}>{p}</span>;
}

function Avatar({ emp, size = 24 }) {
  if (!emp) return null;
  const initials = (emp.full_name || "").split(" ").map(n => n[0]).slice(0, 2).join("");
  return (
    <div
      className="rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0"
      style={{ width: size, height: size }}
      title={emp.full_name}
    >{initials}</div>
  );
}

function EmployeePicker({ employees, value, onChange, placeholder = "Select employee", disabled = false, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64">
        {employees.map(e => (
          <SelectItem key={e.id} value={e.id}>{e.full_name} — {e.designation || e.role || "—"}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FollowersPicker({ employees, value, onChange, excludeIds = [] }) {
  const selected = new Set(value || []);
  const available = employees.filter(e => !selected.has(e.id) && !excludeIds.includes(e.id));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 min-h-[28px]">
        {(value || []).map(id => {
          const e = employees.find(x => x.id === id);
          if (!e) return null;
          return (
            <div key={id} className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full pl-1.5 pr-1 py-0.5">
              <Avatar emp={e} size={18} />
              <span className="text-xs text-slate-700">{e.full_name}</span>
              <button onClick={() => onChange((value || []).filter(v => v !== id))} className="hover:bg-slate-200 rounded-full p-0.5" data-testid={`remove-follower-${id}`}>
                <X className="w-3 h-3 text-slate-500" />
              </button>
            </div>
          );
        })}
      </div>
      <Select value="" onValueChange={(id) => onChange([...(value || []), id])}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="+ Add follower / watcher" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {available.map(e => (
            <SelectItem key={e.id} value={e.id}>{e.full_name} — {e.designation || e.role || "—"}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TaskDetailDrawer({ taskId, open, onClose, onChanged, employees, me }) {
  const [t, setT] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [audit, setAudit] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignNote, setReassignNote] = useState("");

  const load = async () => {
    if (!taskId) return;
    const [r, a] = await Promise.all([
      api.get(`/tasks/${taskId}`),
      api.get(`/tasks/${taskId}/audit`).catch(() => ({ data: [] })),
    ]);
    setT(r.data);
    setAudit(a.data || []);
  };
  useEffect(() => { if (open && taskId) load(); }, [open, taskId]);

  if (!open) return null;

  const setStatus = async (status) => {
    try { await api.patch(`/tasks/${t.id}`, { status }); toast.success("Status updated"); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const setDueDate = async (due_date) => {
    try { await api.patch(`/tasks/${t.id}`, { due_date: due_date || null }); toast.success("Due date updated"); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const sendComment = async () => {
    if (!commentText.trim()) return;
    try { await api.post(`/tasks/${t.id}/comments`, { body: commentText.trim() }); setCommentText(""); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const addFollower = async (eid) => {
    try { await api.post(`/tasks/${t.id}/followers`, { add: [eid], remove: [] }); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const removeFollower = async (eid) => {
    try { await api.post(`/tasks/${t.id}/followers`, { add: [], remove: [eid] }); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const uploadAttachment = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/tasks/${t.id}/attachments`, { name: file.name, data_url: reader.result, size: file.size });
        toast.success("Attached"); load(); onChanged?.();
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };
  const doReassign = async () => {
    if (!reassignTo) return;
    try {
      await api.post(`/tasks/${t.id}/reassign`, { assignee_id: reassignTo, note: reassignNote });
      toast.success("Reassigned"); setReassignOpen(false); setReassignTo(""); setReassignNote(""); load(); onChanged?.();
    } catch { toast.error("Failed"); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this task?")) return;
    try { await api.post(`/tasks/${t.id}/archive`); toast.success("Archived"); onChanged?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete permanently? This cannot be undone.")) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Deleted"); onChanged?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canArchive = t && (me?.role === "super_admin" || me?.role === "hr_admin" || me?.employee_id === t.created_by || me?.employee_id === t.assignee_id);
  const canDelete  = t && (me?.role === "super_admin" || me?.role === "hr_admin" || me?.employee_id === t.created_by);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-drawer">
        {!t ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">TASK</div>
              <DialogTitle className="text-xl">{t.title}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Created by {t.creator?.full_name || "—"} · {new Date(t.created_at).toLocaleString()}
                {t.completed_at && <> · Completed {new Date(t.completed_at).toLocaleString()}</>}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-6 pt-2">
              {/* Main */}
              <div className="space-y-5">
                {t.description && (
                  <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-3">{t.description}</div>
                )}

                {/* Comments */}
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Comments ({t.comments?.length || 0})</div>
                  <div className="space-y-2">
                    {(t.comments || []).map(c => (
                      <div key={c.id} className="bg-white border border-slate-200 rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-slate-800">{c.author_name}</div>
                          <div className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{c.body}</div>
                      </div>
                    ))}
                    {!t.comments?.length && <div className="text-xs text-slate-400">No comments yet.</div>}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Write a comment…" rows={2} className="flex-1" data-testid="task-comment-input" />
                    <Button onClick={sendComment} disabled={!commentText.trim()} className="bg-slate-900 hover:bg-slate-800 self-end" data-testid="task-comment-submit"><Send className="w-4 h-4" /></Button>
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Attachments ({t.attachments?.length || 0})</div>
                  <div className="space-y-1">
                    {(t.attachments || []).map(a => (
                      <a key={a.id} href={a.data_url} download={a.name} className="flex items-center gap-2 text-xs text-slate-700 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1.5 bg-white">
                        <Paperclip className="w-3.5 h-3.5" />
                        <span className="flex-1 truncate">{a.name}</span>
                        <span className="text-slate-400">{a.size ? Math.round(a.size / 1024) + " KB" : ""}</span>
                      </a>
                    ))}
                  </div>
                  <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 cursor-pointer">
                    <Paperclip className="w-3.5 h-3.5" />
                    <span>Attach file</span>
                    <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} data-testid="task-attach-input" />
                  </label>
                </div>

                {/* Audit */}
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Activity ({audit.length})</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                    {audit.map(a => (
                      <div key={a.id} className="text-xs text-slate-600 flex gap-2">
                        <span className="text-slate-400 shrink-0">{new Date(a.at).toLocaleString()}</span>
                        <span className="font-medium text-slate-700 capitalize">{a.action.replace("_", " ")}</span>
                        <span className="text-slate-400">by {a.actor_email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Side metadata */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Status</div>
                  <Select value={t.status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1 h-8" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Priority</div>
                  <Select value={t.priority} onValueChange={(v) => api.patch(`/tasks/${t.id}`, { priority: v }).then(() => { load(); onChanged?.(); })}>
                    <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Assignee</div>
                  <div className="flex items-center gap-2 mt-1">
                    {t.assignee ? <><Avatar emp={t.assignee} size={22} /><span className="text-xs">{t.assignee.full_name}</span></> : <span className="text-xs text-slate-400">unassigned</span>}
                    <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto text-xs" onClick={() => setReassignOpen(true)} data-testid="task-reassign-btn"><RefreshCw className="w-3 h-3 mr-1" />Reassign</Button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Reviewer / Approver</div>
                  <EmployeePicker employees={employees} value={t.reviewer_id} onChange={(v) => api.patch(`/tasks/${t.id}`, { reviewer_id: v }).then(() => { load(); onChanged?.(); })} placeholder="No reviewer" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Followers / Watchers</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(t.follower_details || []).map(f => (
                      <div key={f.id} className="flex items-center gap-1 bg-slate-100 rounded-full pl-1 pr-1 py-0.5">
                        <Avatar emp={f} size={16} />
                        <span className="text-[10px]">{f.full_name}</span>
                        <button onClick={() => removeFollower(f.id)} className="hover:bg-slate-200 rounded-full" data-testid={`drawer-remove-follower-${f.id}`}><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {!t.follower_details?.length && <span className="text-[11px] text-slate-400">None</span>}
                  </div>
                  <Select value="" onValueChange={(id) => addFollower(id)}>
                    <SelectTrigger className="h-7 text-[11px] mt-1.5"><SelectValue placeholder="+ Add follower" /></SelectTrigger>
                    <SelectContent className="max-h-56">
                      {employees.filter(e => !(t.followers || []).includes(e.id)).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Due Date</div>
                  <Input type="date" defaultValue={t.due_date ? t.due_date.slice(0, 10) : ""} onBlur={(e) => setDueDate(e.target.value)} className="mt-1 h-8" data-testid="task-due-date" />
                </div>

                {/* Time tracking */}
                <TaskTimeTracker taskId={t.id} />

                <div className="pt-3 border-t border-slate-200 space-y-2">
                  {canArchive && <Button variant="outline" size="sm" className="w-full" onClick={archive} data-testid="task-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>}
                  {canDelete && <Button variant="outline" size="sm" className="w-full text-red-600 hover:bg-red-50 hover:text-red-700" onClick={del} data-testid="task-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reassign sub-dialog */}
        {reassignOpen && (
          <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Reassign task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>New Assignee</Label>
                  <EmployeePicker employees={employees} value={reassignTo} onChange={setReassignTo} testId="reassign-assignee" />
                </div>
                <div><Label>Handoff note (optional)</Label>
                  <Input value={reassignNote} onChange={(e) => setReassignNote(e.target.value)} placeholder="Context for the new owner…" className="mt-1" data-testid="reassign-note" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={doReassign} disabled={!reassignTo} data-testid="reassign-submit">Reassign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState("mine");
  const [mode, setMode] = useState("table"); // table | board | standup | monthly
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    title: "", description: "",
    priority: "medium", status: "todo",
    project_id: "", department_id: "",
    assignee_id: "", reviewer_id: "",
    followers: [],
    due_date: "",
  });

  const meEmpId = user?.employee_id;

  const load = async () => {
    const [t, p, dep, emp] = await Promise.all([
      api.get("/tasks", { params: { view } }),
      api.get("/projects"),
      api.get("/departments"),
      api.get("/employees"),
    ]);
    setTasks(t.data); setProjects(p.data); setDepartments(dep.data); setEmployees(emp.data);
  };
  useEffect(() => { load(); }, [view]);

  const tloc = useLocation();
  useEffect(() => {
    if (tloc.state?.openAdd) setCreateOpen(true);
    const tid = searchParams.get("task");
    if (tid) setDetailId(tid);
  }, [tloc.state, searchParams]);

  const resetForm = () => setForm({
    title: "", description: "",
    priority: "medium", status: "todo",
    project_id: "", department_id: "",
    assignee_id: meEmpId || "", reviewer_id: "",
    followers: [],
    due_date: "",
  });

  useEffect(() => { if (createOpen && !form.assignee_id) setForm(f => ({ ...f, assignee_id: meEmpId || "" })); // eslint-disable-next-line
  }, [createOpen, meEmpId]);

  const create = async () => {
    try {
      const payload = {
        ...form,
        project_id: form.project_id || null,
        department_id: form.department_id || null,
        reviewer_id: form.reviewer_id || null,
        due_date: form.due_date || null,
        // server will auto-add creator to followers
        followers: form.followers.filter(f => f !== form.assignee_id),
      };
      await api.post("/tasks", payload);
      toast.success("Task created");
      setCreateOpen(false); resetForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const quickStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = useMemo(() => {
    if (!search) return tasks;
    const q = search.toLowerCase();
    return tasks.filter(t =>
      (t.title || "").toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.assignee?.full_name || "").toLowerCase().includes(q)
    );
  }, [tasks, search]);

  const isOverdue = (t) => t.due_date && t.status !== "done" && new Date(t.due_date) < new Date(new Date().toDateString());

  const viewMeta = {
    mine: "Tasks assigned to you or created by you",
    team: "Tasks for you and your direct reports",
    all: user?.role === "super_admin" || user?.role === "hr_admin" ? "All tasks across the organization" : "All tasks visible to you",
    following: "Tasks where you are a watcher",
    overdue: "Unfinished tasks past their due date",
    completed: "Tasks marked as done",
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="tasks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Work Management</div>
          <h1 className="text-2xl sm:text-3xl font-[Outfit] font-bold tracking-tight text-slate-900">Tasks</h1>
          <p className="text-sm text-slate-600 mt-1">{viewMeta[view]} · <span className="font-semibold">{filtered.length}</span> shown</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="task-search" />
          <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="qh-grad-bg text-white font-semibold" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" />New Task</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Task</DialogTitle>
                <DialogDescription>Assign to yourself or someone else, add followers who should stay in the loop, and optionally a reviewer.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
                <div className="md:col-span-2">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="task-title" placeholder="e.g. Prepare Q2 revenue deck" />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1" placeholder="What needs to be done?" />
                </div>
                <div>
                  <Label>Assign To *</Label>
                  <EmployeePicker employees={employees} value={form.assignee_id} onChange={(v) => setForm({ ...form, assignee_id: v })} testId="task-assignee" />
                  <div className="text-[11px] text-slate-500 mt-1">Defaults to you — pick anyone to delegate.</div>
                </div>
                <div>
                  <Label>Reviewer / Approver</Label>
                  <EmployeePicker employees={employees} value={form.reviewer_id} onChange={(v) => setForm({ ...form, reviewer_id: v })} placeholder="Optional" testId="task-reviewer" />
                </div>
                <div className="md:col-span-2">
                  <Label>Followers / Watchers</Label>
                  <FollowersPicker employees={employees} value={form.followers} onChange={(v) => setForm({ ...form, followers: v })} excludeIds={[form.assignee_id].filter(Boolean)} />
                  <div className="text-[11px] text-slate-500 mt-1">You'll be auto-added as a watcher when assigning to someone else.</div>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="No project" /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Department / Team</Label>
                  <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={create} disabled={!form.title || !form.assignee_id} className="bg-slate-900 hover:bg-slate-800" data-testid="task-submit">Create Task</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {VIEWS.map(v => (
            <TabsTrigger key={v.key} value={v.key} data-testid={`view-${v.key}`}>{v.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Display mode switcher — Table / Board / Standup / Monthly */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-lg" data-testid="tasks-mode-switch">
          {[
            {k: "table",   label: "Table",   icon: Table2},
            {k: "board",   label: "Board",   icon: Columns3},
            {k: "standup", label: "Standup", icon: Mic},
            {k: "monthly", label: "Monthly", icon: CalendarDays},
          ].map(m => (
            <button
              key={m.k}
              onClick={() => setMode(m.k)}
              className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition ${mode === m.k ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`mode-${m.k}`}
            >
              <m.icon className="w-3.5 h-3.5"/>{m.label}
            </button>
          ))}
        </div>
        {(mode === "table" || mode === "board") && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={async () => {
              try {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                const token = localStorage.getItem("hreye_token");
                const res = await fetch(`${API_URL}/api/task-sheets/export.csv?scope=${view === "all" ? "all" : view === "team" ? "team" : "my"}`, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `tasks-${view}.csv`; a.click(); URL.revokeObjectURL(url);
                toast.success("Tasks exported");
              } catch { toast.error("Export failed"); }
            }}
            data-testid="tasks-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
          </Button>
        )}
      </div>

      {mode === "table" && (
      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3">Assignee</th>
                <th className="px-4 py-3">Followers</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const od = isOverdue(t);
                return (
                  <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${od ? "bg-red-50/40" : ""}`} onClick={() => setDetailId(t.id)} data-testid={`task-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {t.title}
                        {(t.comments?.length || 0) > 0 && <Badge variant="outline" className="text-[10px] px-1 py-0">{t.comments.length} 💬</Badge>}
                        {(t.attachments?.length || 0) > 0 && <Badge variant="outline" className="text-[10px] px-1 py-0"><Paperclip className="w-2.5 h-2.5" /></Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                        <span>by {t.creator?.full_name || "—"}</span>
                        {projects.find(p => p.id === t.project_id) && <span>· {projects.find(p => p.id === t.project_id).name}</span>}
                        {od && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">OVERDUE</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar emp={t.assignee} size={22} />
                        <span className="text-xs">{t.assignee?.full_name || "unassigned"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex -space-x-1">
                        {(t.follower_details || []).slice(0, 4).map(f => <Avatar key={f.id} emp={f} size={20} />)}
                        {(t.follower_details?.length || 0) > 4 && <div className="w-5 h-5 rounded-full bg-slate-100 text-[9px] flex items-center justify-center text-slate-600 border border-white">+{t.follower_details.length - 4}</div>}
                        {!t.follower_details?.length && <span className="text-[11px] text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><PriorityTag p={t.priority} /></td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={t.status} onValueChange={(v) => quickStatus(t.id, v)}>
                        <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => setDetailId(t.id)} data-testid={`task-open-${t.id}`}><Eye className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">No tasks in this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {mode === "board" && (
        <KanbanBoard tasks={filtered} onStatusChange={(id, s) => quickStatus(id, s)} onOpen={setDetailId} />
      )}
      {mode === "standup" && (
        <StandupSheet user={user} meEmpId={meEmpId} employees={employees} canSeeAll={user?.role === "super_admin" || user?.role === "hr_admin" || user?.role === "manager"} />
      )}
      {mode === "monthly" && (
        <MonthlySheet />
      )}

      <TaskDetailDrawer
        taskId={detailId}
        open={!!detailId}
        onClose={() => { setDetailId(null); searchParams.delete("task"); setSearchParams(searchParams, { replace: true }); }}
        onChanged={load}
        employees={employees}
        me={user}
      />
    </div>
  );
}

/* ==================== KANBAN BOARD ==================== */
const BOARD_COLS = [
  { key: "todo",        label: "To do",       tint: "bg-slate-50",   head: "bg-slate-500" },
  { key: "in_progress", label: "In progress", tint: "bg-cyan-50",    head: "bg-cyan-500" },
  { key: "review",      label: "Review",      tint: "bg-amber-50",   head: "bg-amber-500" },
  { key: "done",        label: "Done",        tint: "bg-emerald-50", head: "bg-emerald-500" },
];

function KanbanBoard({ tasks, onStatusChange, onOpen }) {
  const [draggingId, setDraggingId] = useState(null);
  const cols = BOARD_COLS.map(c => ({ ...c, tasks: tasks.filter(t => t.status === c.key) }));
  const onDragStart = (id) => setDraggingId(id);
  const onDragEnd = () => setDraggingId(null);
  const onDrop = (status) => {
    if (!draggingId) return;
    const task = tasks.find(t => t.id === draggingId);
    if (task && task.status !== status) onStatusChange(draggingId, status);
    setDraggingId(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
      {cols.map(col => (
        <div
          key={col.key}
          className={`rounded-xl ${col.tint} border border-slate-200 min-h-[200px] flex flex-col`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(col.key)}
          data-testid={`col-${col.key}`}
        >
          <div className={`flex items-center justify-between px-4 py-2.5 rounded-t-xl`}>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${col.head}`} />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">{col.label}</span>
            </div>
            <span className="text-[11px] font-semibold text-slate-500 bg-white rounded-full px-2 py-0.5">{col.tasks.length}</span>
          </div>
          <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto max-h-[65vh]">
            {col.tasks.map(t => (
              <div
                key={t.id}
                draggable
                onDragStart={() => onDragStart(t.id)}
                onDragEnd={onDragEnd}
                onClick={() => onOpen(t.id)}
                className={`rounded-lg bg-white border border-slate-200 p-3 shadow-sm hover:shadow-md hover:-translate-y-[1px] cursor-pointer transition-all ${draggingId === t.id ? "opacity-40 scale-95" : ""}`}
                data-testid={`card-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900 flex-1 min-w-0 line-clamp-2">{t.title}</div>
                  <PriorityTag p={t.priority} />
                </div>
                {t.description && <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{t.description}</div>}
                <div className="mt-2.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {t.assignee && <Avatar emp={t.assignee} size={20} />}
                    <span className="text-[11px] text-slate-600 truncate max-w-[90px]">{t.assignee?.full_name || "—"}</span>
                  </div>
                  {t.due_date && (
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />{new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {col.tasks.length === 0 && (
              <div className="h-20 flex items-center justify-center text-[11px] text-slate-400 border-2 border-dashed border-slate-200 rounded-lg m-2">Drop here</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ==================== STANDUP SHEET ==================== */
function StandupSheet({ user, meEmpId, employees, canSeeAll }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      if (date === new Date().toISOString().slice(0, 10)) {
        const { data } = await api.get("/standup/today");
        setMine(data);
      } else {
        const { data } = await api.get("/standup", { params: { date, employee_id: meEmpId } });
        setMine(data[0] || null);
      }
      if (canSeeAll) {
        const { data } = await api.get("/standup", { params: { date } });
        setAll(data);
      } else {
        setAll([]);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const prefill = async () => {
    try { const { data } = await api.post("/standup/pre-fill"); setMine(data); toast.success("Draft generated from your task activity"); }
    catch { toast.error("Could not pre-fill"); }
  };
  const save = async () => {
    if (!mine) return;
    setSaving(true);
    try {
      await api.post("/standup", { date, yesterday: mine.yesterday, today: mine.today, blockers: mine.blockers });
      toast.success("Standup saved"); load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  const exportCsv = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hreye_token");
      const res = await fetch(`${API_URL}/api/standup/export.csv?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `standup_${date}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5" data-testid="standup-sheet">
      {/* Header */}
      <Card className="p-4 border-slate-200 shadow-none flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 text-white flex items-center justify-center">
            <Mic className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Daily Standup</div>
            <div className="text-lg font-[Outfit] font-semibold text-slate-900">Yesterday · Today · Blockers</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="standup-date" />
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="standup-export"><Download className="w-3.5 h-3.5 mr-1.5"/>Export</Button>
        </div>
      </Card>

      {/* My standup */}
      <Card className="p-5 border-cyan-200 bg-gradient-to-br from-cyan-50/30 to-white shadow-none" data-testid="my-standup">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider font-bold text-cyan-700">Your standup</span>
            {mine?.is_draft && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">DRAFT · AI pre-filled</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={prefill} data-testid="standup-prefill"><Sparkles className="w-3.5 h-3.5 mr-1.5"/>AI pre-fill from tasks</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="standup-save">{saving ? "Saving…" : "Save standup"}</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">🌅 Yesterday</Label>
            <Textarea value={mine?.yesterday || ""} onChange={(e) => setMine({ ...(mine || {}), yesterday: e.target.value })} rows={6} className="mt-1 text-xs font-mono" placeholder="What did you accomplish yesterday?" data-testid="standup-yesterday"/>
          </div>
          <div>
            <Label className="text-xs">🎯 Today</Label>
            <Textarea value={mine?.today || ""} onChange={(e) => setMine({ ...(mine || {}), today: e.target.value })} rows={6} className="mt-1 text-xs font-mono" placeholder="What will you work on today?" data-testid="standup-today"/>
          </div>
          <div>
            <Label className="text-xs">🚫 Blockers</Label>
            <Textarea value={mine?.blockers || ""} onChange={(e) => setMine({ ...(mine || {}), blockers: e.target.value })} rows={6} className="mt-1 text-xs font-mono" placeholder="Anything blocking you?" data-testid="standup-blockers"/>
          </div>
        </div>
      </Card>

      {/* Team standups — managers + HR + super_admin */}
      {canSeeAll && all.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-2">Team standups · {date}</div>
          <div className="space-y-2">
            {all.filter(s => s.employee_id !== meEmpId).map(s => (
              <Card key={s.id} className="p-4 border-slate-200 shadow-none" data-testid={`standup-row-${s.employee_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold">{(s.employee_name || "").split(" ").map(x => x[0]).slice(0,2).join("")}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{s.employee_name}</div>
                    <div className="text-[10px] text-slate-500">{s.employee_designation}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div><span className="font-semibold text-slate-700">Yesterday:</span><pre className="mt-1 text-slate-600 whitespace-pre-wrap font-sans text-[11px]">{s.yesterday || "—"}</pre></div>
                  <div><span className="font-semibold text-slate-700">Today:</span><pre className="mt-1 text-slate-600 whitespace-pre-wrap font-sans text-[11px]">{s.today || "—"}</pre></div>
                  <div><span className="font-semibold text-rose-700">Blockers:</span><pre className="mt-1 text-rose-600 whitespace-pre-wrap font-sans text-[11px]">{s.blockers || "—"}</pre></div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== MONTHLY TASK SHEET ==================== */
function MonthlySheet() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/task-sheets/monthly", { params: { year, month } }); setData(data); }
    catch { setData({ rows: [], days: [] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const exportCsv = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hreye_token");
      const res = await fetch(`${API_URL}/api/task-sheets/monthly.csv?year=${year}&month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `tasks_${year}_${String(month).padStart(2,"0")}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const monthName = new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="monthly-sheet">
      <Card className="p-4 border-slate-200 shadow-none flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 text-white flex items-center justify-center">
            <CalendarDays className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Monthly Task Sheet</div>
            <div className="text-lg font-[Outfit] font-semibold text-slate-900">{monthName}</div>
            <div className="text-[10px] text-slate-500">Each cell: Initiated / In-progress / Completed</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={prev} data-testid="monthly-prev"><ChevronLeft className="w-4 h-4"/></Button>
          <Button size="sm" variant="outline" onClick={next} data-testid="monthly-next"><ChevronRight className="w-4 h-4"/></Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="monthly-export" className="ml-2"><Download className="w-3.5 h-3.5 mr-1.5"/>Export</Button>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          {loading && <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>}
          {!loading && data && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700 sticky left-0 bg-white/95 z-20 min-w-[180px]">Employee</th>
                  {data.days.map(d => {
                    const dt = new Date(d);
                    const dow = dt.getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    return (
                      <th key={d} className={`text-center px-1 py-2 font-semibold text-[10px] ${isWeekend ? "text-slate-400" : "text-slate-600"}`}>
                        <div>{dt.getDate()}</div>
                        <div className="text-[9px] opacity-60">{["Su","Mo","Tu","We","Th","Fr","Sa"][dow]}</div>
                      </th>
                    );
                  })}
                  <th className="text-center px-3 py-2 font-semibold text-slate-700 sticky right-0 bg-white/95 z-20">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={data.days.length + 2} className="p-10 text-center text-slate-400">No task activity this month.</td></tr>
                )}
                {data.rows.map(row => (
                  <tr key={row.employee_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="font-semibold text-slate-900">{row.employee_name}</div>
                      {row.designation && <div className="text-[10px] text-slate-500">{row.designation}</div>}
                    </td>
                    {row.days.map(c => {
                      const dt = new Date(c.date);
                      const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                      const has = c.initiated + c.in_progress + c.completed > 0;
                      return (
                        <td key={c.date} className={`text-center px-1 py-1.5 border-l border-slate-100 ${isWeekend ? "bg-slate-50/50" : ""}`}>
                          {has ? (
                            <div className="flex items-center justify-center gap-0.5 text-[10px] font-bold tabular-nums" title={`Initiated ${c.initiated} · In-progress ${c.in_progress} · Completed ${c.completed}`}>
                              {c.initiated > 0   && <span className="text-cyan-600">{c.initiated}</span>}
                              {c.in_progress > 0 && <span className="text-amber-600">·{c.in_progress}</span>}
                              {c.completed > 0   && <span className="text-emerald-600">·{c.completed}</span>}
                            </div>
                          ) : <span className="text-slate-300">·</span>}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right sticky right-0 bg-white z-10 border-l border-slate-100">
                      <div className="flex items-center justify-end gap-2 text-[11px] font-bold">
                        <span className="text-cyan-700">{row.totals.initiated}</span>
                        <span className="text-amber-700">{row.totals.in_progress}</span>
                        <span className="text-emerald-700">{row.totals.completed}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
      <div className="flex items-center gap-4 text-xs text-slate-600 px-2">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-500"/>Initiated</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>In progress</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/>Completed</span>
      </div>
    </div>
  );
}

/* ==================== TASK TIME TRACKER ==================== */
function TaskTimeTracker({ taskId }) {
  const [data, setData] = useState({ total_minutes: 0, entries: [] });
  const [running, setRunning] = useState(null); // active entry
  const [elapsed, setElapsed] = useState(0);
  const [manualMin, setManualMin] = useState("");
  const [manualNote, setManualNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/time`);
      setData(data);
      const act = (data.entries || []).find(e => !e.stopped_at);
      setRunning(act || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  useEffect(() => {
    if (!running) { setElapsed(0); return; }
    const startedMs = new Date(running.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - startedMs) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [running]);

  const start = async () => { try { await api.post(`/tasks/${taskId}/time/start`); await load(); window.dispatchEvent(new CustomEvent("hreye:timer-changed")); toast.success("Timer started"); } catch { toast.error("Failed"); } };
  const stop  = async () => { try { const { data } = await api.post(`/tasks/${taskId}/time/stop`); await load(); window.dispatchEvent(new CustomEvent("hreye:timer-changed")); toast.success(`Logged ${data.minutes}m`); } catch { toast.error("Failed"); } };
  const addManual = async () => {
    const m = parseInt(manualMin, 10);
    if (!m || m <= 0) { toast.error("Enter minutes > 0"); return; }
    try { await api.post(`/tasks/${taskId}/time/manual`, { minutes: m, note: manualNote || null }); setManualMin(""); setManualNote(""); await load(); toast.success("Entry added"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/time-entries/${id}`); await load(); } catch { toast.error("Failed"); } };

  const totalH = Math.floor(data.total_minutes / 60);
  const totalM = data.total_minutes % 60;
  const elapsedH = Math.floor(elapsed / 3600);
  const elapsedM = Math.floor((elapsed % 3600) / 60);
  const elapsedS = elapsed % 60;

  return (
    <div className="pt-3 border-t border-slate-200" data-testid="time-tracker">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5"><Clock className="w-3 h-3"/>Time tracked</div>
        <div className="text-sm font-bold tabular-nums text-slate-900">{totalH}h {String(totalM).padStart(2, "0")}m</div>
      </div>
      {running ? (
        <div className="mt-2 p-2.5 rounded-lg bg-gradient-to-br from-cyan-50 to-sky-50 border border-cyan-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-cyan-700">Timer running</div>
            <div className="text-sm font-bold tabular-nums text-slate-900">{elapsedH}:{String(elapsedM).padStart(2, "0")}:{String(elapsedS).padStart(2, "0")}</div>
          </div>
          <Button size="sm" onClick={stop} className="bg-rose-500 hover:bg-rose-600 h-8" data-testid="timer-stop"><SquareIcon className="w-3.5 h-3.5 mr-1"/>Stop</Button>
        </div>
      ) : (
        <Button size="sm" onClick={start} className="mt-2 w-full bg-cyan-500 hover:bg-cyan-600 h-8" data-testid="timer-start"><Play className="w-3.5 h-3.5 mr-1"/>Start timer</Button>
      )}
      {/* Manual entry */}
      <div className="mt-2 grid grid-cols-[70px_1fr_auto] gap-1">
        <Input type="number" min="1" max="1440" value={manualMin} onChange={(e) => setManualMin(e.target.value)} placeholder="min" className="h-8 text-xs" data-testid="manual-min"/>
        <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Note (optional)" className="h-8 text-xs" data-testid="manual-note"/>
        <Button size="sm" variant="outline" className="h-8 px-2" onClick={addManual} data-testid="manual-add"><Plus className="w-3.5 h-3.5"/></Button>
      </div>
      {/* Entries */}
      {data.entries.length > 0 && (
        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
          {data.entries.slice(0, 10).map(e => (
            <div key={e.id} className="flex items-center justify-between text-[11px] bg-slate-50 rounded px-2 py-1 group">
              <div className="truncate flex-1">
                <span className="font-semibold">{e.minutes}m</span>
                <span className="text-slate-500 ml-2">{new Date(e.started_at).toLocaleDateString(undefined,{month:"short",day:"numeric"})}</span>
                {e.note && <span className="text-slate-600 ml-2 italic truncate">· {e.note}</span>}
              </div>
              <button onClick={() => del(e.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600" data-testid={`time-del-${e.id}`}><X className="w-3 h-3"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
